import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  loadThreadNotificationDefaults,
  saveThreadNotificationDefaults,
  type ThreadNotificationDefaults,
} from '../services/threadNotifications';
import styled, { keyframes } from 'styled-components';
// VersionControl import removed per Miles 2026-04-06
import { saveToLocalStorage, loadFromLocalStorage } from '../utils/helpers';
import { PageContainer } from '../styles/CommonStyles';
import { STORAGE_KEYS } from '../constants';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
// PromptBar import removed per Miles 2026-04-06
import { useResponsive } from '../hooks/useResponsive';
import GlobalFooter from './GlobalFooter';
import Modal from './common/Modal';
import { getAuth } from 'firebase/auth';
import { AnonymousAuthService } from '../utils/anonymousAuth';
import { Paths, debouncedSetDoc, ts, withBackoff } from '../repositories/firestoreUtils';
import { getDoc } from 'firebase/firestore';
import SeoHead from './SeoHead';
import { cleanupTestWalletData, CleanupResult } from '../utils/cleanupTestWalletData';
import { useMaybeGlobalChatContext } from './GlobalChatSystem';
import { setRuntimeTestingEnvironment, isDevPreviewOrigin } from '../config/agenticWorkflowConfig';
import { useUsageVisibility } from '../hooks/useUsageVisibility';

// Add TypeScript declarations for Google APIs
declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          disableAutoSelect: () => void;
        }
      }
    };
    gapi?: {
      auth2?: {
        getAuthInstance: () => {
          signOut: () => Promise<void>;
        };
      }
    };
  }
}

// Viewport meta for proper mobile display. Never disable pinch-zoom
// (maximum-scale / user-scalable) — that is a WCAG 1.4.4 failure.
const ViewportMeta = () => (
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
);

// Add a fadeIn animation for the prompt bar
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animated fade-in wrapper for content sections
const FadeIn = styled.div`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  animation: fadeIn 0.5s ease-out forwards;
`;

// Add a promptFadeIn animation for the prompt bar
const promptFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// MobilePromptBarWrapper removed per Miles 2026-04-06

// Enhanced save indicator
const SaveIndicator = styled.div<{ visible: boolean, isError?: boolean }>`
  position: fixed;
  bottom: 5rem; /* Position above the prompt bar on mobile */
  right: 1rem;
  background: ${props => props.isError ? '#ff5252' : '#4caf50'};
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 30px;
  font-size: 0.875rem;
  opacity: ${props => props.visible ? 1 : 0};
  transform: translateY(${props => props.visible ? 0 : '10px'});
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  
  @media (min-width: 1025px) {
    bottom: 1.5rem;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// GlobalMobileStyle for styling the mobile-prompt-bar-zone
const GlobalMobileStyle = styled.div`
  /* Mobile-first approach */
  .mobile-prompt-bar-zone {
    height: 5rem; /* Space for the prompt bar */
    position: relative;
    width: 100%;
    pointer-events: none;
    margin-top: 1.5rem;
  }
  
  /* Hide version control on mobile and close-to-mobile */
  @media (max-width: 1024px) {
    .version-control-container, 
    [data-testid="version-control"],
    [class*="version-control"],
    [id*="version-control"] {
      display: none;
    }
  }
  
  /* Styles for the builder-style prompt */
  .builder-style-prompt {
    width: 100%;
    height: 100%;
    
    input {
      font-size: 0.9rem;
      height: 36px;
      padding-left: 0.5rem;
      background: transparent;
      border: none;
      flex: 1;
      min-width: 0;
      
      &:focus {
        border-color: transparent;
        box-shadow: none;
      }
    }
    
    button {
      width: 32px; 
      height: 32px;
      padding: 0.25rem;
      min-height: 44px; /* Better touch target */
      
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const SettingsContainer = styled(PageContainer)`
  /* Mobile-first approach - base styles for all devices */
  padding: 1.5rem 1rem;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  /* Bottom padding for the floating prompt bar */
  padding-bottom: 7rem;
  background: linear-gradient(to bottom, var(--brand-canvas), #F5F5F5);
  
  /* Tablet (768px) */
  @media (min-width: 768px) {
    padding: 2rem 1.5rem;
    padding-bottom: 7rem;
  }
  
  /* Laptop (1024px) */
  @media (min-width: 1024px) {
    max-width: 900px;
    padding: 2.5rem;
    min-height: calc(100vh - 160px);
  }
  
  /* Desktop (1200px) */
  @media (min-width: 1200px) {
    max-width: 1000px;
  }
  
  h1 {
    font-family: 'Space Grotesk', sans-serif;
    color: var(--brand-black);
    margin: 0 0 1.75rem 0;
    font-size: 1.75rem;
    font-weight: 500;
    letter-spacing: -0.01em;
    text-transform: lowercase;
    
    @media (min-width: 768px) {
      font-size: 2rem;
      margin: 0 0 2.5rem 0;
    }
  }
`;

const SettingsSection = styled.section`
  /* Mobile-first approach */
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.04);
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  width: 100%;
  
  /* Tablet and above */
  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }
  
  /* Desktop */
  @media (min-width: 1024px) {
    padding: 2.5rem;
    
    &:hover {
      box-shadow: 0 0.75rem 2.5rem rgba(0,0,0,0.08);
      transform: translateY(-2px);
    }
  }
  
  /* Prevent inner content from visually spilling out of the section box */
  overflow: hidden;
`;

const ModernTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  color: var(--brand-black);
  font-size: 1.25rem; /* Smaller on mobile */
  font-weight: 500;
  margin: 0 0 1.5rem 0;
  letter-spacing: -0.01em;
  text-transform: lowercase;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin: 0 0 1.75rem 0;
  }
`;

const SettingGroup = styled.div`
  margin-bottom: 1.75rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2.5rem;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: var(--brand-text-secondary);
  margin: 0.75rem 0 1.5rem;
  line-height: 1.6;
  text-transform: lowercase;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

// ── Notifications (2026-08 revamp): grouped rows + channel chips ───────────
const ChannelLegend = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.625rem;
  margin: 0 0 1.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
  }
`;

const ChannelLegendItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  background: linear-gradient(145deg, var(--brand-canvas), #ffffff);
  border: 1px solid var(--brand-line);
  border-radius: 12px;

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: var(--brand-gold-hover);
    margin-top: 0.1rem;
  }
`;

const ChannelLegendText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

const ChannelLegendName = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--brand-black);
  text-transform: lowercase;
`;

const ChannelLegendHint = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  color: var(--brand-text-secondary);
  line-height: 1.35;
  text-transform: lowercase;
`;

const NotifGroup = styled.div`
  background: white;
  border: 1px solid var(--brand-line);
  border-radius: 14px;
  padding: 1.125rem 1.125rem 0.25rem;
  margin-bottom: 1.25rem;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover {
    border-color: #DDD4B8;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  }

  @media (min-width: 768px) {
    padding: 1.5rem 1.5rem 0.5rem;
    border-radius: 16px;
  }
`;

const NotifGroupHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid #F0EDE4;
`;

const NotifGroupTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--brand-black);
  margin: 0;
  text-transform: lowercase;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
    color: var(--brand-gold-hover);
    flex-shrink: 0;
  }

  @media (min-width: 768px) {
    font-size: 1.05rem;
  }
`;

const NotifGroupCaption = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.75rem;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
`;

const NotifRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid #F0EDE4;

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
  }
`;

const NotifInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const NotifName = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--brand-black);
  text-transform: lowercase;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const NotifDesc = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.75rem;
  color: var(--brand-text-secondary);
  line-height: 1.4;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ThresholdPill = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.25rem;
  padding: 0.3rem 0.7rem;
  background: #FBF8F0;
  border: 1px solid #E8DFC4;
  border-radius: 999px;
  color: #8B7333;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: lowercase;
  transition: all 0.2s ease;

  svg {
    width: 11px;
    height: 11px;
  }

  &:hover {
    border-color: var(--brand-gold-alt);
    color: #7A5C00;
    background: #FAF8F0;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(200, 167, 94, 0.25);
  }
`;

const ChannelChips = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const ChannelChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 40px;
  padding: 0.45rem 0.95rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: lowercase;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.$active ? 'transparent' : 'var(--brand-line)'};
  background: ${props => props.$active
    ? 'linear-gradient(135deg, var(--brand-gold-alt) 0%, var(--brand-gold-hover) 100%)'
    : '#ffffff'};
  color: ${props => props.$active ? '#ffffff' : '#6b7280'};
  box-shadow: ${props => props.$active
    ? '0 3px 10px rgba(182, 146, 76, 0.28)'
    : 'none'};

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }

  &:hover {
    border-color: ${props => props.$active ? 'transparent' : 'var(--brand-gold-alt)'};
    color: ${props => props.$active ? '#ffffff' : 'var(--brand-gold-hover)'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(200, 167, 94, 0.3);
  }
`;

const Toast = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%) ${props => props.show ? 'translateY(0)' : 'translateY(100px)'};
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: ${props => props.show ? 1 : 0};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1);
  font-weight: 500;
  backdrop-filter: blur(8px);
  
  @media (max-width: 480px) {
    bottom: 6rem;
    font-size: 0.85rem;
    padding: 0.875rem 1.5rem;
    border-radius: 24px;
    left: 1rem;
    right: 1rem;
    transform: translateX(0) ${props => props.show ? 'translateY(0)' : 'translateY(100px)'};
  }
  
  @media (min-width: 481px) and (max-width: 640px) {
    bottom: 6rem;
  }
  
  svg {
    flex-shrink: 0;
  }
`;

const ThresholdInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1rem;
  background: var(--brand-canvas);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--brand-gold-hover);
    background: white;
    box-shadow: 0 0 0 3px rgba(200, 167, 94, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e1;
  }
`;

// ── Linked sections (2026-08 reorder): wallet / team / integrations /
// subscription live on their own pages; settings renders them as full-width
// link cards below notifications, in that fixed order. ──────────────────────
const QuickLinksStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const LinkSectionCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 1rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.05);
  padding: 1.125rem 1.25rem;
  text-decoration: none;
  transition: all 0.25s ease;

  @media (min-width: 768px) {
    padding: 1.375rem 1.75rem;
    border-radius: 1.25rem;
  }

  &:hover {
    border-color: #E8DFC4;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(200, 167, 94, 0.3);
  }
`;

const LinkIconSquare = styled.span`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--brand-gold-alt) 0%, var(--brand-gold-hover) 100%);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 19px;
    height: 19px;
  }
`;

const LinkTexts = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  flex: 1;
`;

const LinkTitle = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--brand-black);
  text-transform: lowercase;
  letter-spacing: -0.01em;
`;

const LinkDesc = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  color: var(--brand-text-secondary);
  line-height: 1.4;
`;

const LinkChevron = styled.span`
  color: var(--brand-gold-alt);
  flex-shrink: 0;
  display: inline-flex;
  transition: transform 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  ${LinkSectionCard}:hover & {
    transform: translateX(3px);
  }
`;

const ErrorMessage = styled.div`
  color: #f44336;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  text-transform: lowercase;
  padding: 0.5rem 0.75rem;
  background-color: rgba(244, 67, 54, 0.08);
  border-radius: 0.5rem;
  border-left: 3px solid #f44336;
`;

// Admin section styled components
const PinContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 100%;
  
  @media (min-width: 768px) {
    max-width: 400px;
  }
`;

const PinInputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  
  @media (min-width: 768px) {
    gap: 0.75rem;
    justify-content: flex-start;
  }
`;

const PinDigit = styled.input`
  width: 3.5rem;
  height: 3.5rem;
  text-align: center;
  font-size: 1.5rem;
  border: 1px solid var(--brand-line);
  border-radius: 0.5rem;
  -moz-appearance: textfield;
  
  /* Ensure minimum touch target size */
  min-width: 44px;
  min-height: 44px;
  
  &::-webkit-inner-spin-button, 
  &::-webkit-outer-spin-button { 
    -webkit-appearance: none;
    margin: 0;
  }
  
  &:focus {
    outline: none;
    border-color: var(--brand-gold-alt);
    box-shadow: 0 0 0 3px rgba(200, 167, 94, 0.1);
  }
  
  @media (min-width: 768px) {
    width: 4rem;
    height: 4rem;
  }
`;

interface AdminStyledProps {
  disabled?: boolean;
  active?: boolean;
}

const AdminButton = styled.button<AdminStyledProps>`
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid var(--brand-gold-alt);
  border-radius: 0.5rem;
  background: ${props => props.disabled ? '#e9e9e9' : 'transparent'};
  color: ${props => props.disabled ? '#999' : 'var(--brand-gold-alt)'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.9rem;
  font-weight: 400;
  transition: all 0.3s ease;
  text-transform: lowercase;
  /* Ensure minimum touch target size */
  min-height: 44px;
  
  &:hover, &:focus {
    background: ${props => props.disabled ? '#e9e9e9' : 'var(--brand-gold-alt)'};
    color: ${props => props.disabled ? '#999' : 'white'};
    outline: none;
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.98)'};
  }
`;

const AdminButtonDanger = styled(AdminButton)`
  border-color: #dc3545;
  color: #dc3545;
  margin-top: 0.75rem;

  &:hover, &:focus {
    background: ${props => props.disabled ? '#e9e9e9' : '#dc3545'};
    color: ${props => props.disabled ? '#999' : 'white'};
  }
`;

const AdminDivider = styled.div`
  margin: 1.5rem 0 1rem;
  border-top: 1px solid var(--brand-line);
`;

const CleanupResultSummary = styled.div`
  margin-top: 0.5rem;

  .result-row {
    display: flex;
    justify-content: space-between;
    padding: 0.375rem 0;
    font-size: 0.875rem;

    &.total {
      border-top: 1px solid #dee2e6;
      margin-top: 0.5rem;
      padding-top: 0.625rem;
      font-weight: 600;
    }
  }

  .label {
    color: var(--brand-text-secondary);
  }

  .count {
    color: var(--brand-black);
    font-weight: 500;
  }
`;

const DangerButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  background: #dc3545;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  text-transform: lowercase;
  min-height: 44px;

  &:hover {
    background: #c82333;
  }

  &:disabled {
    background: #e57373;
    cursor: not-allowed;
  }
`;

const AdminStatus = styled.div<AdminStyledProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: ${props => props.active ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)'};
  color: ${props => props.active ? '#28a745' : '#dc3545'};
`;

const StatusIcon = styled.span<AdminStyledProps>`
  display: inline-block;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background: ${props => props.active ? '#28a745' : '#dc3545'};
  
  @media (min-width: 768px) {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

// Collapsible section for mobile view
const CollapsibleSection = styled.div<{ isCollapsed: boolean }>`
  overflow: hidden;
  max-height: ${props => props.isCollapsed ? '60px' : '2000px'};
  transition: max-height 0.5s ease-in-out;
  position: relative;
  
  &::after {
    content: '';
    display: ${props => props.isCollapsed ? 'block' : 'none'};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1));
    pointer-events: none;
  }
  
  @media (min-width: 768px) {
    max-height: none;
    overflow: visible;
    
    &::after {
      display: none;
    }
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: var(--brand-gold-alt);
  font-size: 0.875rem;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  min-height: 44px; /* Better touch target */
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    color: var(--brand-gold-hover);
    text-decoration: underline;
    outline: none;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    transition: transform 0.3s ease;
  }
  
  @media (min-width: 768px) {
    display: none;
  }
`;

// Simple FooterWrapper that doesn't interfere with Footer's responsive design
const FooterWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;


// ── Notification categories (2026-08 revamp) ────────────────────────────────
// Grouped around what the wallet actually does: money in/out on top, account
// and platform news below. Field names persisted to Firestore are unchanged.
type NotificationCategoryId =
  | 'lowOlvBalance'
  | 'tokenEarningsReceived'
  | 'largeSpendReceipt'
  | 'bankDepositStatus'
  | 'accountSecurity'
  | 'systemMaintenance'
  | 'featurePolicyDigest'
  // Workstation & billing lifecycle (idle-lifecycle completion, 2026-08-18).
  // Server-side categories shipped 2026-08-17/18 (notifications/dispatch.ts
  // billingLifecycle / workspaceLifecycle); these rows give users the same
  // channel control the other categories have.
  | 'billingLifecycle'
  | 'workspaceLifecycle';

type NotificationChannel = 'platform' | 'email' | 'text';

interface NotificationItemDef {
  id: NotificationCategoryId;
  label: string;
  description: string;
  thresholdKey?: 'lowOlvBalance' | 'largeSpendReceipt';
}

interface NotificationGroupDef {
  title: string;
  caption: string;
  iconKey: 'wallet' | 'shield' | 'desktop';
  items: NotificationItemDef[];
}

const NOTIFICATION_GROUPS: NotificationGroupDef[] = [
  {
    title: 'wallet & balance',
    caption: 'money moving in and out of your wallet',
    iconKey: 'wallet',
    items: [
      {
        id: 'lowOlvBalance',
        label: 'low balance',
        description: 'when your balance falls below your alert amount',
        thresholdKey: 'lowOlvBalance'
      },
      {
        id: 'largeSpendReceipt',
        label: 'large spend receipt',
        description: 'a receipt whenever a single run spends more than your set amount',
        thresholdKey: 'largeSpendReceipt'
      },
      {
        id: 'tokenEarningsReceived',
        label: 'earnings received',
        description: 'when new earnings land in your wallet'
      },
      {
        id: 'bankDepositStatus',
        label: 'deposits & payouts',
        description: 'top-ups, auto top-up activity, and bank payout status'
      }
    ]
  },
  {
    title: 'account & platform',
    caption: 'security alerts and service updates',
    iconKey: 'shield',
    items: [
      {
        id: 'accountSecurity',
        label: 'account security',
        description: 'new logins and password changes'
      },
      {
        id: 'systemMaintenance',
        label: 'system maintenance',
        description: 'planned or urgent downtime'
      },
      {
        id: 'featurePolicyDigest',
        label: 'product & policy digest',
        description: 'a short monthly summary of what changed'
      }
    ]
  },
  {
    title: 'workstation & billing',
    caption: 'your dedicated workstation and subscription health',
    iconKey: 'desktop',
    items: [
      {
        id: 'billingLifecycle',
        label: 'billing & workstation warnings',
        description: 'payment failures, trial endings, and workstation shutdown notices'
      },
      {
        id: 'workspaceLifecycle',
        label: 'workstation sleep & wake',
        description: 'quiet-workstation notices, sleep confirmations, and "it\'s back" wake alerts'
      }
    ]
  }
];

const NOTIFICATION_GROUP_ICONS: Record<'wallet' | 'shield' | 'desktop', React.ReactNode> = {
  desktop: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2"></rect>
      <line x1="8" y1="21" x2="16" y2="21"></line>
      <line x1="12" y1="17" x2="12" y2="21"></line>
    </svg>
  ),
  wallet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path>
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path>
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path>
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
    </svg>
  )
};

// Linked management surfaces (2026-08 reorder). Rendered below the
// notifications section in exactly this order per owner directive:
// profile → wallet → team & roles → integrations → subscription.
const SETTINGS_QUICK_LINKS: { to: string; title: string; description: string; icon: React.ReactNode }[] = [
  {
    to: '/profile',
    title: 'profile',
    description: 'your name, phone, and personal details — verify your phone for text notifications',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4"></circle>
        <path d="M4 21v-1a8 8 0 0 1 16 0v1"></path>
      </svg>
    )
  },
  {
    to: '/wallet',
    title: 'wallet',
    description: 'balance, top-ups, payment methods, and spending limits',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path>
        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path>
        <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path>
      </svg>
    )
  },
  {
    to: '/settings/team',
    title: 'team & roles',
    description: 'manage team members, roles, and client portal access',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    )
  },
  {
    to: '/integrations',
    title: 'integrations',
    description: 'connect email, calendars, and the other services Prest0n works with',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22v-5"></path>
        <path d="M9 8V2"></path>
        <path d="M15 8V2"></path>
        <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"></path>
      </svg>
    )
  },
  {
    to: '/subscription',
    title: 'subscription',
    description: 'your plan, billing cycle, and upgrades',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
        <line x1="2" y1="10" x2="22" y2="10"></line>
      </svg>
    )
  }
];

const Settings: React.FC = () => {
  // Centralized auth
  const { logout } = useAuth();
  // Global chat context for immediate UI updates on cleanup
  const chatContext = useMaybeGlobalChatContext();
  const isAuthenticatedRef = useRef(true);
  // Admin mode + PIN state
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [testingEnvironment, setTestingEnvironment] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);

  // Add state for save indicator
  const [saveIndicator, setSaveIndicator] = useState({
    visible: false,
    isError: false,
    message: ''
  });
  
  // Add state for mobile view detection using the responsive hook
  const { isMobile, isTablet, isLaptop } = useResponsive();
  const { canSeeWallet } = useUsageVisibility();
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Add state for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState({
    adminAccess: false,
    account: false
  });
  
  // Notification preferences state
  // Thread notification DEFAULTS (owner directive 2026-08-26): what a NEW
  // thread / group thread starts with. Established threads keep their own
  // stamped choice — see services/threadNotifications.resolveThreadNotifyMode.
  const [threadNotifyDefaults, setThreadNotifyDefaults] = useState<ThreadNotificationDefaults>(
    () => loadThreadNotificationDefaults(getAuth().currentUser?.uid),
  );
  const handleThreadDefaultToggle = (
    kind: 'thread' | 'groupThread',
    channel: 'chime' | 'platform' | 'email' | 'text',
  ) => {
    setThreadNotifyDefaults((prev) => {
      const next: ThreadNotificationDefaults = {
        ...prev,
        [kind]: { ...prev[kind], [channel]: !prev[kind][channel] },
      };
      saveThreadNotificationDefaults(next, getAuth().currentUser?.uid);
      return next;
    });
    // Firestore persistence + save confirmation happen via the settings effect
  };
  const [notificationPrefs, setNotificationPrefs] = useState(() => 
    loadFromLocalStorage('notificationPrefs', {
      lowOlvBalance: { platform: true, email: true, text: false },
      tokenEarningsReceived: { platform: true, email: false, text: false },
      largeSpendReceipt: { platform: false, email: true, text: false },
      bankDepositStatus: { platform: false, email: true, text: true },
      accountSecurity: { platform: false, email: true, text: true },
      systemMaintenance: { platform: false, email: true, text: false },
      featurePolicyDigest: { platform: false, email: true, text: false },
      // Mirror the server DEFAULT_CHANNELS (functions/src/notifications/
      // dispatch.ts): billing dunning is deliberately loud, sleep/wake quieter.
      billingLifecycle: { platform: true, email: true, text: true },
      workspaceLifecycle: { platform: true, email: true, text: false }
    })
  );
  
  // Threshold states
  const [thresholds, setThresholds] = useState(() =>
    loadFromLocalStorage('notificationThresholds', {
      lowOlvBalance: 10,
      largeSpendReceipt: 100
    })
  );
  
  // Modal states
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [currentThresholdType, setCurrentThresholdType] = useState<'lowOlvBalance' | 'largeSpendReceipt'>('lowOlvBalance');
  const [tempThresholdValue, setTempThresholdValue] = useState(0);
  
  // Toast state (reserved; not used for save confirmations)
  const [showToast] = useState(false);
  // Track last known server save timestamp and manage single save indicator flow
  const lastSavedServerTsRef = useRef<number | null>(null);
  const saveGenerationRef = useRef(0);
  const savePollTimeoutRef = useRef<number | null>(null);
  const saveHideTimeoutRef = useRef<number | null>(null);
  // Legacy server-stored anthropic api key: the api-settings UI was removed
  // (2026-08), but an existing stored key must survive saves — profile_settings
  // is written as a whole array, so dropping the field here would erase it.
  const preservedAnthropicKeyRef = useRef('');

  // Firebase auth helpers
  const firebaseAuth = getAuth();
  const anonymousAuthService = useRef(AnonymousAuthService.getInstance());

  // Debounced save to Firestore: users/{uid}/user_settings/user_settings (captures latest state)
  const saveUserSettings = useCallback(async () => {
    try {
      let firebaseUser = firebaseAuth.currentUser;
      if (!firebaseUser) {
        try { await anonymousAuthService.current.ensureAuthenticated(); } catch { return; }
        firebaseUser = firebaseAuth.currentUser;
      }
      const uid = firebaseUser?.uid;
      if (!uid) return;

      const ref = Paths.userSettingsDoc(uid);
      const settingsMap: any = {
        admin_access: Boolean(adminMode),
        olv_balance_notification_threashold: String(thresholds.lowOlvBalance ?? ''),
        large_spend_threashold: String(thresholds.largeSpendReceipt ?? ''),
        low_olv_balance_platform_notification: Boolean(notificationPrefs.lowOlvBalance?.platform),
        low_olv_balance_email_notification: Boolean(notificationPrefs.lowOlvBalance?.email),
        low_olv_balance_text_notification: Boolean(notificationPrefs.lowOlvBalance?.text),
        token_earnings_recieved_platform_notification: Boolean(notificationPrefs.tokenEarningsReceived?.platform),
        token_earnings_recieved_email_notification: Boolean(notificationPrefs.tokenEarningsReceived?.email),
        token_earnings_recieved_text_notification: Boolean(notificationPrefs.tokenEarningsReceived?.text),
        large_spend_threashold_platform_notification: Boolean(notificationPrefs.largeSpendReceipt?.platform),
        large_spend_threashold_email_notification: Boolean(notificationPrefs.largeSpendReceipt?.email),
        large_spend_threashold_text_notification: Boolean(notificationPrefs.largeSpendReceipt?.text),
        bank_deposit_status_platform_notification: Boolean(notificationPrefs.bankDepositStatus?.platform),
        bank_deposit_status_email_notification: Boolean(notificationPrefs.bankDepositStatus?.email),
        bank_deposit_status_text_notification: Boolean(notificationPrefs.bankDepositStatus?.text),
        account_security_platform_notification: Boolean(notificationPrefs.accountSecurity?.platform),
        account_security_email_notification: Boolean(notificationPrefs.accountSecurity?.email),
        account_security_text_notification: Boolean(notificationPrefs.accountSecurity?.text),
        system_maintenance_platform_notification: Boolean(notificationPrefs.systemMaintenance?.platform),
        system_maintenance_email_notification: Boolean(notificationPrefs.systemMaintenance?.email),
        system_maintenance_text_notification: Boolean(notificationPrefs.systemMaintenance?.text),
        feature_and_policy_digest_platform_notification: Boolean(notificationPrefs.featurePolicyDigest?.platform),
        feature_and_policy_digest_email_notification: Boolean(notificationPrefs.featurePolicyDigest?.email),
        feature_and_policy_digest_text_notification: Boolean(notificationPrefs.featurePolicyDigest?.text),
        billing_lifecycle_platform_notification: Boolean(notificationPrefs.billingLifecycle?.platform ?? true),
        billing_lifecycle_email_notification: Boolean(notificationPrefs.billingLifecycle?.email ?? true),
        billing_lifecycle_text_notification: Boolean(notificationPrefs.billingLifecycle?.text ?? true),
        workspace_lifecycle_platform_notification: Boolean(notificationPrefs.workspaceLifecycle?.platform ?? true),
        workspace_lifecycle_email_notification: Boolean(notificationPrefs.workspaceLifecycle?.email ?? true),
        workspace_lifecycle_text_notification: Boolean(notificationPrefs.workspaceLifecycle?.text ?? false),
        // Thread notification defaults (2026-08-26): consumed by the
        // notification dispatch plane for the email/text channels; the chime
        // and platform behavior live client-side (services/threadNotifications).
        thread_default_chime: Boolean(threadNotifyDefaults.thread.chime),
        thread_default_platform_notification: Boolean(threadNotifyDefaults.thread.platform),
        thread_default_email_notification: Boolean(threadNotifyDefaults.thread.email),
        thread_default_text_notification: Boolean(threadNotifyDefaults.thread.text),
        group_thread_default_chime: Boolean(threadNotifyDefaults.groupThread.chime),
        group_thread_default_platform_notification: Boolean(threadNotifyDefaults.groupThread.platform),
        group_thread_default_email_notification: Boolean(threadNotifyDefaults.groupThread.email),
        group_thread_default_text_notification: Boolean(threadNotifyDefaults.groupThread.text),
        testing_environment: Boolean(testingEnvironment)
      };
      if (preservedAnthropicKeyRef.current) settingsMap.anthropic_apikey = preservedAnthropicKeyRef.current;

      const payload: any = {
        user_settings_lastupdated: ts.serverNow(),
        profile_settings: [settingsMap]
      };

      // Begin save UX
      setSaveIndicator({ visible: true, isError: false, message: 'saving…' });
      await debouncedSetDoc(ref, payload, { debounceKey: ref.path, debounceMs: 2000 });

      // Ensure we only show one confirmation after the write lands
      const thisGen = ++saveGenerationRef.current;
      if (savePollTimeoutRef.current) {
        window.clearTimeout(savePollTimeoutRef.current);
        savePollTimeoutRef.current = null;
      }
      if (saveHideTimeoutRef.current) {
        window.clearTimeout(saveHideTimeoutRef.current);
        saveHideTimeoutRef.current = null;
      }

      const expected = settingsMap;
      const startAt = Date.now();
      const maxWaitMs = 10000;

      const keysToCheck = Object.keys(expected);
      const isMatch = (remote: any) => keysToCheck.every((k) => {
        const a = remote?.[k];
        const b = (expected as any)[k];
        return String(a) === String(b);
      });

      const pollOnce = async () => {
        if (saveGenerationRef.current !== thisGen) return; // superseded by a newer save
        try {
          const snap = await getDoc(ref);
          const data = snap.exists() ? (snap.data() as any) : null;
          const first = Array.isArray(data?.profile_settings) ? data.profile_settings[0] : null;
          const serverTs = data?.user_settings_lastupdated;
          const serverMs = serverTs?.toMillis ? serverTs.toMillis() : null;
          const advanced = serverMs && (!lastSavedServerTsRef.current || serverMs > lastSavedServerTsRef.current);
          if (first && isMatch(first) && advanced) {
            lastSavedServerTsRef.current = serverMs;
            if (saveGenerationRef.current === thisGen) {
              setSaveIndicator({ visible: true, isError: false, message: 'saved ✓' });
              saveHideTimeoutRef.current = window.setTimeout(() => {
                if (saveGenerationRef.current === thisGen) {
                  setSaveIndicator((s) => ({ ...s, visible: false }));
                }
                if (saveHideTimeoutRef.current) {
                  window.clearTimeout(saveHideTimeoutRef.current);
                  saveHideTimeoutRef.current = null;
                }
              }, 900);
            }
            return; // done
          }
        } catch {}
        // schedule next poll or give up
        if (Date.now() - startAt < maxWaitMs && saveGenerationRef.current === thisGen) {
          savePollTimeoutRef.current = window.setTimeout(pollOnce, 350);
        } else if (saveGenerationRef.current === thisGen) {
          // Fallback: hide saving indicator without duplicate confirmations
          setSaveIndicator((s) => ({ ...s, visible: false }));
        }
      };
      savePollTimeoutRef.current = window.setTimeout(pollOnce, 500);
    } catch (e) {
      console.error('Failed to save user settings:', e);
      setSaveIndicator({ visible: true, isError: true, message: 'save failed' });
      setTimeout(() => setSaveIndicator((s) => ({ ...s, visible: false })), 1800);
    }
  }, [firebaseAuth, adminMode, testingEnvironment, thresholds, notificationPrefs, threadNotifyDefaults]);
  
  // Persist settings AFTER state commits, and only once the server copy has
  // hydrated. Calling saveUserSettings() directly inside a handler right after
  // setState captured the previous closure and silently saved stale values
  // (the latest toggle was always missing); this effect always runs with the
  // fresh values. Three hydration states guard WHEN it may write:
  //  - 'pending': before the Firestore load resolves — never write, so a
  //    pre-load commit (e.g. adminMode restored from localStorage) can't
  //    clobber the server doc or drop the preserved anthropic key. A FAILED
  //    load stays 'pending' for the same reason.
  //  - 'echo': the load-back commit itself — swallowed, so a plain page visit
  //    never writes.
  //  - 'ready': user-driven commits persist.
  const hydrationRef = useRef<'pending' | 'echo' | 'ready'>('pending');
  useEffect(() => {
    if (hydrationRef.current === 'pending') return;
    if (hydrationRef.current === 'echo') {
      hydrationRef.current = 'ready';
      return;
    }
    saveUserSettings();
  }, [adminMode, testingEnvironment, notificationPrefs, thresholds, threadNotifyDefaults, saveUserSettings]);

  // Toggle function for collapsible sections
  const toggleSection = (section: keyof typeof collapsedSections) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Handle resize events to detect mobile view
  useEffect(() => {
    // Handle mobile viewport
    const handleResize = () => {
      // Check if mobile view or close to mobile view (for consistent experience)
      const isMobile = window.innerWidth <= 768;
      const isCloseToMobile = window.innerWidth <= 1024;
      setIsMobileView(isMobile || isCloseToMobile);
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Use effect to check admin mode from localStorage on mount
  useEffect(() => {
    const savedAdminMode = loadFromLocalStorage(STORAGE_KEYS.ADMIN_MODE, false);
    setAdminMode(savedAdminMode);
  }, []);

  // Initial load from Firestore into UI
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        let user = firebaseAuth.currentUser;
        if (!user) {
          // Wait briefly for Firebase auth state to propagate instead of redirecting
          // ProtectedRoute already verified user has access
          await new Promise(resolve => setTimeout(resolve, 500));
          user = firebaseAuth.currentUser;
          if (!user) return; // Firebase still not ready, skip loading
        }
        const uid = user?.uid;
        if (!uid || canceled) return;
        const ref = Paths.userSettingsDoc(uid);
        const snap = await withBackoff(() => getDoc(ref));
        if (canceled) return;
        if (!snap.exists()) {
          // Nothing stored yet — no echo commit will follow, arm saves directly.
          hydrationRef.current = 'ready';
          return;
        }
        const data = snap.data() as any;
        const first = Array.isArray(data?.profile_settings) ? data.profile_settings[0] : null;
        if (first) {
          // Absent fields keep the current (default) value instead of coercing
          // to false — legacy docs predate some categories, and an explicit
          // stored false must stay distinguishable from "never set" because the
          // backend dispatcher applies the same typeof-boolean rule.
          const pickBool = (v: unknown, fallback: boolean): boolean =>
            typeof v === 'boolean' ? v : fallback;
          if (typeof first.admin_access === 'boolean') {
            setAdminMode(first.admin_access);
            saveToLocalStorage(STORAGE_KEYS.ADMIN_MODE, first.admin_access);
          }
          if (typeof first.testing_environment === 'boolean') {
            setTestingEnvironment(first.testing_environment);
            setRuntimeTestingEnvironment(first.testing_environment);
          }
          const lowOlv = parseInt(first.olv_balance_notification_threashold, 10);
          const largeSpend = parseInt(first.large_spend_threashold, 10);
          setThresholds((prev: any) => ({
            ...prev,
            lowOlvBalance: Number.isNaN(lowOlv) || lowOlv <= 0 ? prev.lowOlvBalance : lowOlv,
            largeSpendReceipt: Number.isNaN(largeSpend) || largeSpend <= 0 ? prev.largeSpendReceipt : largeSpend
          }));
          setNotificationPrefs((prev: any) => ({
            ...prev,
            lowOlvBalance: {
              platform: pickBool(first.low_olv_balance_platform_notification, prev.lowOlvBalance.platform),
              email: pickBool(first.low_olv_balance_email_notification, prev.lowOlvBalance.email),
              text: pickBool(first.low_olv_balance_text_notification, prev.lowOlvBalance.text)
            },
            tokenEarningsReceived: {
              platform: pickBool(first.token_earnings_recieved_platform_notification, prev.tokenEarningsReceived.platform),
              email: pickBool(first.token_earnings_recieved_email_notification, prev.tokenEarningsReceived.email),
              text: pickBool(first.token_earnings_recieved_text_notification, prev.tokenEarningsReceived.text)
            },
            largeSpendReceipt: {
              platform: pickBool(first.large_spend_threashold_platform_notification, prev.largeSpendReceipt.platform),
              email: pickBool(first.large_spend_threashold_email_notification, prev.largeSpendReceipt.email),
              text: pickBool(first.large_spend_threashold_text_notification, prev.largeSpendReceipt.text)
            },
            bankDepositStatus: {
              platform: pickBool(first.bank_deposit_status_platform_notification, prev.bankDepositStatus.platform),
              email: pickBool(first.bank_deposit_status_email_notification, prev.bankDepositStatus.email),
              text: pickBool(first.bank_deposit_status_text_notification, prev.bankDepositStatus.text)
            },
            accountSecurity: {
              platform: pickBool(first.account_security_platform_notification, prev.accountSecurity.platform),
              email: pickBool(first.account_security_email_notification, prev.accountSecurity.email),
              text: pickBool(first.account_security_text_notification, prev.accountSecurity.text)
            },
            systemMaintenance: {
              platform: pickBool(first.system_maintenance_platform_notification, prev.systemMaintenance.platform),
              email: pickBool(first.system_maintenance_email_notification, prev.systemMaintenance.email),
              text: pickBool(first.system_maintenance_text_notification, prev.systemMaintenance.text)
            },
            featurePolicyDigest: {
              platform: pickBool(first.feature_and_policy_digest_platform_notification, prev.featurePolicyDigest.platform),
              email: pickBool(first.feature_and_policy_digest_email_notification, prev.featurePolicyDigest.email),
              text: pickBool(first.feature_and_policy_digest_text_notification, prev.featurePolicyDigest.text)
            },
            // Optional-chained fallbacks: a pre-2026-08-18 localStorage cache
            // won't carry these keys yet.
            billingLifecycle: {
              platform: pickBool(first.billing_lifecycle_platform_notification, prev.billingLifecycle?.platform ?? true),
              email: pickBool(first.billing_lifecycle_email_notification, prev.billingLifecycle?.email ?? true),
              text: pickBool(first.billing_lifecycle_text_notification, prev.billingLifecycle?.text ?? true)
            },
            workspaceLifecycle: {
              platform: pickBool(first.workspace_lifecycle_platform_notification, prev.workspaceLifecycle?.platform ?? true),
              email: pickBool(first.workspace_lifecycle_email_notification, prev.workspaceLifecycle?.email ?? true),
              text: pickBool(first.workspace_lifecycle_text_notification, prev.workspaceLifecycle?.text ?? false)
            }
          }));
          // Thread notification defaults round-trip (2026-08-27 review fix):
          // saved-but-never-hydrated meant any save from another device
          // clobbered the server copy with THAT device's local (usually
          // factory) values. Same pickBool discipline — absent keeps current.
          setThreadNotifyDefaults((prev) => {
            const next: ThreadNotificationDefaults = {
              thread: {
                chime: pickBool(first.thread_default_chime, prev.thread.chime),
                platform: pickBool(first.thread_default_platform_notification, prev.thread.platform),
                email: pickBool(first.thread_default_email_notification, prev.thread.email),
                text: pickBool(first.thread_default_text_notification, prev.thread.text),
              },
              groupThread: {
                chime: pickBool(first.group_thread_default_chime, prev.groupThread.chime),
                platform: pickBool(first.group_thread_default_platform_notification, prev.groupThread.platform),
                email: pickBool(first.group_thread_default_email_notification, prev.groupThread.email),
                text: pickBool(first.group_thread_default_text_notification, prev.groupThread.text),
              },
            };
            saveThreadNotificationDefaults(next, firebaseAuth.currentUser?.uid);
            return next;
          });
          if (typeof first.anthropic_apikey === 'string' && first.anthropic_apikey) {
            preservedAnthropicKeyRef.current = first.anthropic_apikey;
          }
          // setNotificationPrefs above always commits a new object, so exactly
          // one echo run of the persistence effect follows this batch.
          hydrationRef.current = 'echo';
        } else {
          hydrationRef.current = 'ready';
        }
      } catch (e) {
        // Load failed — hydrationRef stays 'pending' on purpose: we must never
        // overwrite server settings we couldn't read.
        console.warn('Failed to load user settings from Firestore:', e);
      }
    })();
    return () => { canceled = true; };
  }, [firebaseAuth]);

  // Handle PIN digit input
  const handlePinDigitChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(0, 1);
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newPinDigits = [...pinDigits];
    newPinDigits[index] = value;
    setPinDigits(newPinDigits);
    
    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    
    // Clear any previous errors
    setPinError('');
    setPinSuccess(false);
  };

  // Handle PIN key down for backspace
  const handlePinKeyDown = (index: number, e: React.KeyboardEvent) => {
    // When backspace is pressed and the current field is empty, focus the previous field
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Validate PIN and enable admin mode if correct
  const validatePin = () => {
    const enteredPin = pinDigits.join('');
    const correctPin = '1230';
    
    if (enteredPin === correctPin) {
      setAdminMode(true);
      saveToLocalStorage(STORAGE_KEYS.ADMIN_MODE, true);
      setPinSuccess(true);

      // Dispatch a custom event to notify components about admin mode change
      // (Firestore persistence happens via the settings effect above.)
      const event = new CustomEvent('admin-mode-changed', {
        detail: { adminMode: true }
      });
      window.dispatchEvent(event);
    } else {
      setAdminMode(false);
      saveToLocalStorage(STORAGE_KEYS.ADMIN_MODE, false);
      setPinError('Invalid PIN. Please try again.');
      // Clear the PIN inputs
      setPinDigits(['', '', '', '']);
      // Focus on the first digit
      const firstInput = document.getElementById('pin-0');
      if (firstInput) firstInput.focus();
    }
  };

  // Disable admin mode
  const disableAdminMode = () => {
    setAdminMode(false);
    saveToLocalStorage(STORAGE_KEYS.ADMIN_MODE, false);
    setPinDigits(['', '', '', '']);
    setPinSuccess(false);

    // Dispatch a custom event to notify components about admin mode change
    // (Firestore persistence happens via the settings effect above.)
    const event = new CustomEvent('admin-mode-changed', {
      detail: { adminMode: false }
    });
    window.dispatchEvent(event);
  };

  const toggleTestingEnvironment = () => {
    const newValue = !testingEnvironment;
    setTestingEnvironment(newValue);
    setRuntimeTestingEnvironment(newValue);
    window.dispatchEvent(new CustomEvent('testing-environment-changed', { detail: { testingEnvironment: newValue } }));
  };

  // Test data cleanup handlers
  const handleOpenCleanupModal = () => {
    setCleanupResult(null);
    setShowCleanupModal(true);
  };

  const handleCloseCleanupModal = () => {
    setShowCleanupModal(false);
    setCleanupResult(null);
  };

  const handleCleanupTestData = async () => {
    setIsCleaningUp(true);
    setCleanupResult(null);

    try {
      const result = await cleanupTestWalletData({
        onMessagesCleared: (remainingMessages) => {
          // Update UI state immediately if chat context is available
          if (chatContext?.dispatch) {
            chatContext.dispatch({
              type: 'SET_MESSAGES',
              payload: remainingMessages.map(m => ({
                ...m,
                timestamp: m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp),
              })),
            });
          }
        },
      });
      setCleanupResult(result);
    } catch (error) {
      setCleanupResult({
        expenses: 0,
        earnings: 0,
        usage: 0,
        transactions: 0,
        messages: 0,
        total: 0,
        error: error instanceof Error ? error.message : 'Cleanup failed'
      });
    } finally {
      setIsCleaningUp(false);
    }
  };

  // Notification handlers
  const handleNotificationToggle = (
    notificationId: keyof typeof notificationPrefs,
    channel: 'platform' | 'email' | 'text'
  ) => {
    // A pre-2026-08-18 localStorage cache may lack newly added categories
    // (billingLifecycle / workspaceLifecycle) — never crash the toggle.
    const current = (notificationPrefs as any)[notificationId]
      ?? { platform: false, email: false, text: false };

    // Check for security warning
    if (notificationId === 'accountSecurity') {
      const newPrefs = {
        ...current,
        [channel]: !current[channel]
      };

      // Check if all channels will be disabled
      if (!newPrefs.platform && !newPrefs.email && !newPrefs.text) {
        setShowSecurityModal(true);
        return;
      }
    }

    const updatedPrefs = {
      ...notificationPrefs,
      [notificationId]: {
        ...current,
        [channel]: !current[channel]
      }
    };

    setNotificationPrefs(updatedPrefs);
    saveToLocalStorage('notificationPrefs', updatedPrefs);
    // Firestore persistence + save confirmation happen via the settings effect
  };
  
  const handleThresholdEdit = (type: 'lowOlvBalance' | 'largeSpendReceipt') => {
    setCurrentThresholdType(type);
    setTempThresholdValue(thresholds[type]);
    setShowThresholdModal(true);
  };
  
  const handleThresholdSave = () => {
    // Clamp to ≥ $1: the backend dispatcher treats a non-positive stored
    // threshold as "unset" and silently falls back to its defaults.
    const updatedThresholds = {
      ...thresholds,
      [currentThresholdType]: Math.max(1, Math.round(tempThresholdValue) || 1)
    };
    
    setThresholds(updatedThresholds);
    saveToLocalStorage('notificationThresholds', updatedThresholds);
    setShowThresholdModal(false);
    // Firestore persistence + save confirmation happen via the settings effect
  };
  
  const confirmDisableAllSecurity = () => {
    const updatedPrefs = {
      ...notificationPrefs,
      accountSecurity: { platform: false, email: false, text: false }
    };
    
    setNotificationPrefs(updatedPrefs);
    saveToLocalStorage('notificationPrefs', updatedPrefs);
    setShowSecurityModal(false);
    // Firestore persistence + save confirmation happen via the settings effect
  };

  return (
    <>
      <SeoHead
        tabTitle="settings"
        title="Account Settings | prest0"
        description="manage your prest0 account settings, authentication, and platform preferences"
      />
      {/* Add viewport meta tag for proper mobile display */}
      <ViewportMeta />
    
      <SettingsContainer>
        {/* Add GlobalMobileStyle to apply mobile-specific styling */}
        <GlobalMobileStyle />
      
        <FadeIn>
          <h1>Settings</h1>

          {/* Notifications lead the page (2026-08 owner directive) — they are
              the one thing configured HERE; everything else links out below. */}
          <SettingsSection>
            <ModernTitle>notifications</ModernTitle>
            <SettingGroup>
              <Description>
                choose how you hear about wallet activity and account events — in the platform, by email, or by text message
              </Description>

              <ChannelLegend>
                <ChannelLegendItem>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  <ChannelLegendText>
                    <ChannelLegendName>platform</ChannelLegendName>
                    <ChannelLegendHint>the notification bell in your prest0 header</ChannelLegendHint>
                  </ChannelLegendText>
                </ChannelLegendItem>
                <ChannelLegendItem>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                  <ChannelLegendText>
                    <ChannelLegendName>email</ChannelLegendName>
                    <ChannelLegendHint>sent to your account email address</ChannelLegendHint>
                  </ChannelLegendText>
                </ChannelLegendItem>
                <ChannelLegendItem>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <ChannelLegendText>
                    <ChannelLegendName>text</ChannelLegendName>
                    <ChannelLegendHint>texts go to your verified phone — add or verify it on your profile page</ChannelLegendHint>
                  </ChannelLegendText>
                </ChannelLegendItem>
              </ChannelLegend>

              {NOTIFICATION_GROUPS.map(group => (
                <NotifGroup key={group.title}>
                  <NotifGroupHeader>
                    <NotifGroupTitle>
                      {NOTIFICATION_GROUP_ICONS[group.iconKey]}
                      {group.title}
                    </NotifGroupTitle>
                    <NotifGroupCaption>{group.caption}</NotifGroupCaption>
                  </NotifGroupHeader>

                  {group.items.map(item => (
                    <NotifRow key={item.id}>
                      <NotifInfo>
                        <NotifName>{item.label}</NotifName>
                        <NotifDesc>{item.description}</NotifDesc>
                        {item.thresholdKey && (
                          <ThresholdPill
                            type="button"
                            onClick={() => handleThresholdEdit(item.thresholdKey!)}
                          >
                            {item.thresholdKey === 'lowOlvBalance'
                              ? `alerts below $${thresholds.lowOlvBalance}`
                              : `receipts over $${thresholds.largeSpendReceipt}`}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                            </svg>
                          </ThresholdPill>
                        )}
                      </NotifInfo>
                      <ChannelChips>
                        {(['platform', 'email', 'text'] as NotificationChannel[]).map(channel => {
                          const active = Boolean(notificationPrefs[item.id]?.[channel]);
                          return (
                            <ChannelChip
                              key={channel}
                              type="button"
                              $active={active}
                              aria-pressed={active}
                              aria-label={`${item.label} via ${channel}`}
                              onClick={() => handleNotificationToggle(item.id, channel)}
                            >
                              {active ? (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                  <line x1="12" y1="5" x2="12" y2="19"></line>
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                              )}
                              {channel}
                            </ChannelChip>
                          );
                        })}
                      </ChannelChips>
                    </NotifRow>
                  ))}
                </NotifGroup>
              ))}

              {/* Thread notification DEFAULTS (owner directive 2026-08-26).
                  These set what a NEW thread or group thread starts with; a
                  thread the user already has keeps the choice stamped on it —
                  including anything set from the bell in the chat header. */}
              <NotifGroup data-thread-notify-defaults="">
                <NotifGroupHeader>
                  <NotifGroupTitle>
                    {NOTIFICATION_GROUP_ICONS.desktop}
                    thread defaults
                  </NotifGroupTitle>
                  <NotifGroupCaption>
                    how NEW threads start out — threads you already have keep their own setting
                    (change those from the bell at the top of the chat)
                  </NotifGroupCaption>
                </NotifGroupHeader>

                {([
                  { kind: 'thread' as const, chime: true, label: 'threads — chime', desc: 'a quick chime when a new message arrives' },
                  { kind: 'thread' as const, chime: false, label: 'threads — notifications', desc: 'platform, email, and text notifications per new message' },
                  { kind: 'groupThread' as const, chime: true, label: 'group threads — chime', desc: 'a quick chime when a teammate or Prest0n posts' },
                  { kind: 'groupThread' as const, chime: false, label: 'group threads — notifications', desc: 'platform, email, and text notifications per new message' },
                ]).map((row) => (
                  <NotifRow key={`${row.kind}-${row.chime ? 'chime' : 'channels'}`}>
                    <NotifInfo>
                      <NotifName>{row.label}</NotifName>
                      <NotifDesc>{row.desc}</NotifDesc>
                    </NotifInfo>
                    <ChannelChips>
                      {(row.chime
                        ? (['chime'] as const)
                        : (['platform', 'email', 'text'] as const)
                      ).map((channel) => {
                        const active = Boolean(threadNotifyDefaults[row.kind][channel]);
                        return (
                          <ChannelChip
                            key={channel}
                            type="button"
                            $active={active}
                            aria-pressed={active}
                            aria-label={`${row.label} via ${channel}`}
                            onClick={() => handleThreadDefaultToggle(row.kind, channel)}
                          >
                            {active ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            )}
                            {channel}
                          </ChannelChip>
                        );
                      })}
                    </ChannelChips>
                  </NotifRow>
                ))}
              </NotifGroup>
            </SettingGroup>
          </SettingsSection>

          {/* Linked sections — profile, wallet, team & roles, integrations,
              subscription (in that order): each manages its own page; settings
              links to them the way the team & roles section always did. */}
          <QuickLinksStack>
            {/* Task #77: billing surfaces (wallet + subscription) share one
                visibility rule — owner or billing.view (fail-open until RBAC
                resolves). Members without it don't get the entries at all. */}
            {SETTINGS_QUICK_LINKS.filter(
              link => canSeeWallet || (link.to !== '/subscription' && link.to !== '/wallet'),
            ).map(link => (
              <LinkSectionCard key={link.to} to={link.to}>
                <LinkIconSquare aria-hidden="true">{link.icon}</LinkIconSquare>
                <LinkTexts>
                  <LinkTitle>{link.title}</LinkTitle>
                  <LinkDesc>{link.description}</LinkDesc>
                </LinkTexts>
                <LinkChevron aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </LinkChevron>
              </LinkSectionCard>
            ))}
          </QuickLinksStack>

          {/* Admin Section with collapsible content */}
          <SettingsSection>
            <ModernTitle>admin access</ModernTitle>
            <SettingGroup>
              <Description>
                enter the 4-digit pin to enable admin mode and unlock advanced features, admin mode provides access to system configuration and special settings
              </Description>
              
              <CollapsibleSection isCollapsed={collapsedSections.adminAccess}>
                {adminMode ? (
                  <>
                    <AdminStatus active>
                      <StatusIcon active />
                      admin mode is currently active
                    </AdminStatus>
                    <Description>
                      admin features are now accessible, you can disable admin mode at any time
                    </Description>
                    <AdminButton onClick={disableAdminMode}>
                      disable admin mode
                    </AdminButton>

                    {/* Testing Environment Toggle */}
                    <AdminDivider />
                    <Description>
                      testing mode tags all wallet records as "test example" so they can be identified and removed later — ai model quality is unchanged, and no wallet balance is deducted while active
                      {isDevPreviewOrigin() && !testingEnvironment && (
                        <span style={{ display: 'block', marginTop: '0.4rem', color: '#888', fontSize: '0.82rem' }}>
                          note: dev/preview environments auto-enable test tagging regardless of this toggle
                        </span>
                      )}
                    </Description>
                    <AdminButton
                      onClick={toggleTestingEnvironment}
                      style={testingEnvironment ? { borderColor: '#28a745', color: '#28a745' } : undefined}
                    >
                      {testingEnvironment ? 'testing mode: on' : 'testing mode: off'}
                    </AdminButton>

                    {/* Test Data Cleanup */}
                    <AdminDivider />
                    <Description>
                      remove all wallet records tagged as "test example" - includes test expenses, earnings, usage, and transactions
                    </Description>
                    <AdminButtonDanger
                      onClick={handleOpenCleanupModal}
                      disabled={isCleaningUp}
                    >
                      {isCleaningUp ? 'removing...' : 'remove test data'}
                    </AdminButtonDanger>
                  </>
                ) : (
                  <>
                    <PinContainer>
                      <PinInputRow>
                        {pinDigits.map((digit, index) => (
                          <PinDigit
                            key={index}
                            id={`pin-${index}`}
                            type="number"
                            value={digit}
                            onChange={(e) => handlePinDigitChange(index, e.target.value)}
                            onKeyDown={(e) => handlePinKeyDown(index, e)}
                            maxLength={1}
                            autoComplete="off"
                            aria-label={`PIN digit ${index + 1}`}
                          />
                        ))}
                      </PinInputRow>
                      
                      {pinError && <ErrorMessage>{pinError}</ErrorMessage>}
                      {pinSuccess && (
                        <AdminStatus active>
                          <StatusIcon active />
                          pin verified successfully
                        </AdminStatus>
                      )}
                      
                      <AdminButton 
                        onClick={validatePin}
                        disabled={pinDigits.some(digit => !digit)}
                      >
                        validate pin
                      </AdminButton>
                    </PinContainer>
                  </>
                )}
              </CollapsibleSection>
              
              {/* Toggle button for mobile view */}
              <ToggleButton onClick={() => toggleSection('adminAccess')}>
                {collapsedSections.adminAccess ? 'Show more' : 'Show less'}
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ transform: collapsedSections.adminAccess ? 'rotate(0deg)' : 'rotate(180deg)' }}
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </ToggleButton>
            </SettingGroup>
          </SettingsSection>

          {/* Logout Section with collapsible content */}
          <SettingsSection>
            <ModernTitle>account</ModernTitle>
            <SettingGroup>
              <Description>
                manage your account settings and session, logging out will end your current session and return you to the login screen
              </Description>
              
              <CollapsibleSection isCollapsed={collapsedSections.account}>
                <AdminButton
                  onClick={() => {
                    // Block the persistence effect FIRST: the state resets below
                    // would otherwise queue a debounced write of hard-coded
                    // defaults that races the async Firebase sign-out and can
                    // overwrite the real account's settings.
                    hydrationRef.current = 'pending';

                    // Handle Google Sign-out if the user is signed in with Google
                    const handleGoogleLogout = () => {
                      // Check if Google Auth is available
                      if (window.google && window.google.accounts && window.google.accounts.id) {
                        // Revoke Google authentication and clear state
                        window.google.accounts.id.disableAutoSelect();
                        
                        // If gapi is available, also sign out from there
                        if (window.gapi && window.gapi.auth2) {
                          const auth2 = window.gapi.auth2.getAuthInstance();
                          if (auth2) {
                            auth2.signOut().then(() => {
                              console.log('User signed out from Google.');
                            });
                          }
                        }
                      }
                    };

                    // Execute Google logout
                    handleGoogleLogout();
                    
                    // Use centralized logout to clear token and all Builder/Profile/Settings caches
                    try { logout(); } catch {}
                    
                    // Additionally clear Settings-specific local caches without touching Firestore
                    // API keys now managed server-side only - no localStorage clearing needed
                    try { localStorage.removeItem(STORAGE_KEYS.ADMIN_MODE); } catch {}
                    try { localStorage.removeItem('notificationPrefs'); } catch {}
                    try { localStorage.removeItem('notificationThresholds'); } catch {}
                    // Purge keys from retired settings (global autonomy + limits moved off this page)
                    try { localStorage.removeItem('global_autonomy_level'); } catch {}
                    try { localStorage.removeItem('planning_stage_limit'); } catch {}
                    try { localStorage.removeItem('cost_limit_usd'); } catch {}

                    // Reset in-memory Settings state to defaults
                    setAdminMode(false);
                    setNotificationPrefs({
                      lowOlvBalance: { platform: true, email: true, text: false },
                      tokenEarningsReceived: { platform: true, email: false, text: false },
                      largeSpendReceipt: { platform: false, email: true, text: false },
                      bankDepositStatus: { platform: false, email: true, text: true },
                      accountSecurity: { platform: false, email: true, text: true },
                      systemMaintenance: { platform: false, email: true, text: false },
                      featurePolicyDigest: { platform: false, email: true, text: false },
                      billingLifecycle: { platform: true, email: true, text: true },
                      workspaceLifecycle: { platform: true, email: true, text: false }
                    });
                    setThresholds({ lowOlvBalance: 10, largeSpendReceipt: 100 });
                    preservedAnthropicKeyRef.current = '';
                    setPinDigits(['', '', '', '']);
                    setPinError('');
                    setPinSuccess(false);

                    // Centralized logout takes care of navigation and reload
                  }}
                >
                  log out
                </AdminButton>
              </CollapsibleSection>
              
              {/* Toggle button for mobile view */}
              <ToggleButton onClick={() => toggleSection('account')}>
                {collapsedSections.account ? 'Show more' : 'Show less'}
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ transform: collapsedSections.account ? 'rotate(0deg)' : 'rotate(180deg)' }}
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </ToggleButton>
            </SettingGroup>
          </SettingsSection>
        </FadeIn>
        
        {/* Save indicator */}
        <SaveIndicator 
          visible={saveIndicator.visible} 
          isError={saveIndicator.isError}
        >
          {saveIndicator.isError ? null : <Spinner />}
          {saveIndicator.message}
        </SaveIndicator>
        
        {/* Add a spacer div at the bottom to ensure content isn't hidden behind the toolbar */}
        <div className="mobile-prompt-bar-zone"></div>
        
        {/* VersionControl and PromptBar removed — GlobalChatSystem will be added later */}
      </SettingsContainer>
      
      {/* Notification Modals */}
      <Modal
        isOpen={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        title="are you sure?"
        description="you may miss critical security alerts."
        size="small"
        footer={
          <>
            <button
              onClick={() => setShowSecurityModal(false)}
              style={{
                background: 'none',
                border: '1px solid var(--brand-line)',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                color: 'var(--brand-text-secondary)',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textTransform: 'lowercase'
              }}
            >
              cancel
            </button>
            <button
              onClick={confirmDisableAllSecurity}
              style={{
                background: 'var(--brand-gold-alt)',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                color: 'white',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textTransform: 'lowercase'
              }}
            >
              confirm
            </button>
          </>
        }
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          disabling all security notifications means you won't receive alerts for important account changes like new logins or password changes.
        </div>
      </Modal>

      <Modal
        isOpen={showThresholdModal}
        onClose={() => setShowThresholdModal(false)}
        title={`edit ${currentThresholdType === 'lowOlvBalance' ? 'balance threshold' : 'spend amount'}`}
        size="small"
        footer={
          <>
            <button
              onClick={() => setShowThresholdModal(false)}
              style={{
                background: 'none',
                border: '1px solid var(--brand-line)',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                color: 'var(--brand-text-secondary)',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textTransform: 'lowercase'
              }}
            >
              cancel
            </button>
            <button
              onClick={handleThresholdSave}
              style={{
                background: 'var(--brand-gold-alt)',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                color: 'white',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textTransform: 'lowercase'
              }}
            >
              save
            </button>
          </>
        }
      >
        <div style={{ padding: '1rem 0' }}>
          <label 
            htmlFor="threshold-input"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: '0.875rem',
              color: 'var(--brand-black)',
              textTransform: 'lowercase'
            }}
          >
            {currentThresholdType === 'lowOlvBalance' ? 'minimum balance (usd)' : 'spend amount (usd)'}
          </label>
          <ThresholdInput
            id="threshold-input"
            type="number"
            min="1"
            step="1"
            value={tempThresholdValue}
            onChange={(e) => setTempThresholdValue(parseInt(e.target.value) || 0)}
            placeholder="Enter amount"
          />
        </div>
      </Modal>

      {/* Test Data Cleanup Confirmation Modal */}
      <Modal
        isOpen={showCleanupModal}
        onClose={handleCloseCleanupModal}
        title="remove test data?"
        description="this will permanently delete all wallet records with the 'test example' tag."
        size="small"
        footer={
          cleanupResult ? (
            <button
              onClick={handleCloseCleanupModal}
              style={{
                background: 'var(--brand-gold-alt)',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                color: 'white',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textTransform: 'lowercase'
              }}
            >
              done
            </button>
          ) : (
            <>
              <button
                onClick={handleCloseCleanupModal}
                disabled={isCleaningUp}
                style={{
                  background: 'none',
                  border: '1px solid var(--brand-line)',
                  borderRadius: '8px',
                  padding: '0.75rem 1.5rem',
                  color: 'var(--brand-text-secondary)',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  fontSize: '0.875rem',
                  cursor: isCleaningUp ? 'not-allowed' : 'pointer',
                  textTransform: 'lowercase',
                  opacity: isCleaningUp ? 0.5 : 1
                }}
              >
                cancel
              </button>
              <DangerButton
                onClick={handleCleanupTestData}
                disabled={isCleaningUp}
              >
                {isCleaningUp ? 'removing...' : 'remove all'}
              </DangerButton>
            </>
          )
        }
      >
        {cleanupResult ? (
          <CleanupResultSummary>
            {cleanupResult.error ? (
              <div style={{ color: '#dc3545' }}>
                Error: {cleanupResult.error}
              </div>
            ) : (
              <>
                <div className="result-row">
                  <span className="label">expenses</span>
                  <span className="count">{cleanupResult.expenses}</span>
                </div>
                <div className="result-row">
                  <span className="label">earnings</span>
                  <span className="count">{cleanupResult.earnings}</span>
                </div>
                <div className="result-row">
                  <span className="label">usage records</span>
                  <span className="count">{cleanupResult.usage}</span>
                </div>
                <div className="result-row">
                  <span className="label">transactions</span>
                  <span className="count">{cleanupResult.transactions}</span>
                </div>
                <div className="result-row">
                  <span className="label">chat messages</span>
                  <span className="count">{cleanupResult.messages}</span>
                </div>
                <div className="result-row total">
                  <span className="label">total removed</span>
                  <span className="count">{cleanupResult.total}</span>
                </div>
              </>
            )}
          </CleanupResultSummary>
        ) : (
          <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
            <p style={{ margin: '0 0 1rem', color: 'var(--brand-text-secondary)', fontSize: '0.9rem' }}>
              this action cannot be undone. the following will be deleted:
            </p>
            <ul style={{
              textAlign: 'left',
              margin: '0 auto',
              display: 'inline-block',
              padding: '0 0 0 1.5rem',
              color: 'var(--brand-black)',
              fontSize: '0.875rem',
              lineHeight: '1.8'
            }}>
              <li>expenses with "test example" tag</li>
              <li>earnings with "test example" tag</li>
              <li>usage records with "test example" tag</li>
              <li>transactions with "test example" tag</li>
              <li>chat messages with "test example" tag</li>
            </ul>
          </div>
        )}
      </Modal>

      {/* Toast */}
      <Toast show={showToast}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
        </svg>
        saved ✓
      </Toast>
      
      <FooterWrapper>
        <GlobalFooter />
      </FooterWrapper>
    </>
  );
};

export default Settings; 